/**
 * Generated Chat Images
 *
 * Extracts Losi-generated image URLs from assistant message markdown.
 *
 * Primary responsibilities:
 * - Parse generated image markdown from persisted chat messages
 * - Return stable image records for header galleries
 *
 * Dependencies:
 * - lib/media/safe-url
 */

import { getSafeRenderableImageUrl } from '@/lib/media/safe-url'

export type GeneratedChatImageMessage = {
  id: string
  content?: unknown
  created_at?: string | null
  role?: string | null
  sequence_number?: number | null
}

export type GeneratedChatImage = {
  id: string
  url: string
  alt: string
  name: string
  messageId: string
}

const GENERATED_IMAGE_MARKDOWN_PATTERN =
  /!\[([^\]]*)\]\((\S*generated_images\S*?)(?:\s+["'][^"']*["'])?\)/gi

// --------------

function compareNewestMessages(
  left: GeneratedChatImageMessage,
  right: GeneratedChatImageMessage
) {
  const leftSequence = left.sequence_number ?? Number.NEGATIVE_INFINITY
  const rightSequence = right.sequence_number ?? Number.NEGATIVE_INFINITY

  if (leftSequence !== rightSequence) {
    return rightSequence - leftSequence
  }

  const leftTime = left.created_at ? Date.parse(left.created_at) : 0
  const rightTime = right.created_at ? Date.parse(right.created_at) : 0

  return rightTime - leftTime
}

// --------------

function getImageName(alt: string, index: number) {
  const trimmedAlt = alt.trim()

  if (trimmedAlt) {
    return trimmedAlt
  }

  return `Generated image ${index + 1}`
}

// --------------

export function extractGeneratedChatImages(
  messages: GeneratedChatImageMessage[]
): GeneratedChatImage[] {
  const seenUrls = new Set<string>()
  const images: GeneratedChatImage[] = []

  const orderedMessages = [...messages].sort(compareNewestMessages)

  for (const message of orderedMessages) {
    if (message.role && message.role !== 'assistant') {
      continue
    }

    if (typeof message.content !== 'string') {
      continue
    }

    const matches = message.content.matchAll(GENERATED_IMAGE_MARKDOWN_PATTERN)

    for (const match of matches) {
      const safeUrl = getSafeRenderableImageUrl(match[2])

      if (!safeUrl || seenUrls.has(safeUrl)) {
        continue
      }

      seenUrls.add(safeUrl)

      const alt = match[1]?.trim() || 'Generated image'
      const name = getImageName(alt, images.length)

      images.push({
        id: `${message.id}:${images.length}:${safeUrl}`,
        url: safeUrl,
        alt,
        name,
        messageId: message.id
      })
    }
  }

  return images
}
